import type { ComponentType } from 'react'
import { Activity, Archive, BarChart3, BookOpen, Calculator, CalendarCheck, CalendarDays, CalendarHeart, CheckSquare, ClipboardList, Crosshair, Dog, FileBarChart, FileSignature, FileText, ImageIcon, LayoutDashboard, MessageCircle, Package, Receipt, Settings, Target, UserCheck, Users, Wallet, Workflow } from 'lucide-react'
import type { PersonaKey } from '@/shared/domain'
import { isPublicEventsEnabled, isSeasonRecapEnabled } from '@/shared/config'

export type NavEntry = {
  id: string
  label: string
  icon: ComponentType<{ className?: string }>
  to?: string
  personas: PersonaKey[]
  children?: NavEntry[]
  badgeCount?: number
  isEnabled?: () => boolean
  flattenForPersonas?: PersonaKey[]
}

export const NAV_ENTRIES: NavEntry[] = [
  {
    id: 'dashboard',
    label: 'Command Center',
    icon: LayoutDashboard,
    to: '/dashboard',
    personas: [
      'club-manager',
      'outfitter-owner',
    ],
  },
  {
    id: 'bookings',
    label: 'Bookings',
    icon: CalendarCheck,
    personas: ['outfitter-owner'],
    children: [
      {
        id: 'bookings-overview',
        label: 'Bookings',
        icon: ClipboardList,
        to: '/bookings',
        personas: ['outfitter-owner'],
      },
      {
        id: 'clients',
        label: 'Customers',
        icon: UserCheck,
        to: '/clients',
        personas: ['outfitter-owner'],
      },
      {
        id: 'finances-invoices',
        label: 'Invoices',
        icon: Receipt,
        to: '/finances/invoices',
        personas: ['outfitter-owner'],
      },
      {
        id: 'finances-booking-packages',
        label: 'Booking Packages',
        icon: Package,
        to: '/finances/booking-packages',
        personas: ['outfitter-owner'],
      },
    ],
  },
  {
    id: 'finances',
    label: 'Finances',
    icon: Wallet,
    personas: ['outfitter-owner', 'club-manager'],
    children: [
      {
        id: 'finances-expenses',
        label: 'Expense Manager',
        icon: Calculator,
        to: '/finances/expenses',
        personas: ['outfitter-owner', 'club-manager'],
      },
      {
        id: 'finances-reports',
        label: 'Financial Reports',
        icon: FileBarChart,
        to: '/finances/reports',
        personas: ['outfitter-owner', 'club-manager'],
      },
      {
        id: 'finances-member-ledger',
        label: 'Member Ledger',
        icon: BookOpen,
        to: '/finances/member-ledger',
        personas: ['outfitter-owner', 'club-manager'],
      },
    ],
  },
  {
    id: 'hunts',
    label: 'Hunts',
    icon: Crosshair,
    personas: ['club-manager', 'outfitter-owner'],
    children: [
      {
        id: 'hunt-logs',
        label: 'Hunt Log',
        icon: Crosshair,
        to: '/hunt-logs',
        personas: ['club-manager', 'outfitter-owner'],
      },
      {
        id: 'analytics',
        label: 'Analytics',
        icon: BarChart3,
        to: '/analytics',
        personas: ['club-manager', 'outfitter-owner'],
      },
      {
        id: 'season-recap',
        label: 'Season Recap',
        icon: BookOpen,
        to: '/season-recap',
        personas: ['club-manager', 'outfitter-owner'],
        isEnabled: isSeasonRecapEnabled,
      },
      {
        id: 'doghouse',
        label: 'Doghouse',
        icon: Dog,
        to: '/doghouse',
        personas: ['club-manager', 'outfitter-owner'],
      },
      {
        id: 'historical',
        label: 'Season Archives',
        icon: Archive,
        to: '/historical',
        personas: ['club-manager', 'outfitter-owner'],
      },
      {
        id: 'reports',
        label: 'Reports',
        icon: FileText,
        to: '/reports',
        personas: ['club-manager', 'outfitter-owner'],
      },
    ],
  },
  {
    id: 'operations',
    label: 'Operations',
    icon: Workflow,
    personas: ['club-manager', 'outfitter-owner'],
    flattenForPersonas: ['club-manager'],
    children: [
      {
        id: 'events',
        label: 'Events',
        icon: CalendarHeart,
        to: '/events',
        personas: ['outfitter-owner'],
        isEnabled: isPublicEventsEnabled,
      },
      {
        id: 'activity',
        label: 'Activity',
        icon: Activity,
        to: '/activity',
        personas: ['club-manager', 'outfitter-owner'],
      },
      {
        id: 'connect-messages',
        label: 'Messages',
        icon: MessageCircle,
        to: '/messages',
        personas: ['outfitter-owner', 'club-manager'],
      },
    ],
  },
  {
    id: 'schedule',
    label: 'Calendar',
    icon: CalendarDays,
    to: '/schedule',
    personas: ['club-manager', 'outfitter-owner'],
  },
  {
    id: 'properties',
    label: 'Mapping',
    icon: Target,
    to: '/properties',
    personas: ['club-manager', 'outfitter-owner'],
  },
  {
    id: 'todos',
    label: 'To-Dos',
    icon: CheckSquare,
    to: '/todos',
    personas: ['club-manager', 'outfitter-owner'],
  },
  {
    id: 'inventory',
    label: 'Inventory',
    icon: Package,
    to: '/inventory',
    personas: ['club-manager', 'outfitter-owner'],
  },
  {
    id: 'gallery',
    label: 'Gallery',
    icon: ImageIcon,
    to: '/gallery',
    personas: ['club-manager', 'outfitter-owner'],
  },
  {
    id: 'org-roster',
    label: 'Org Roster',
    icon: Users,
    to: '/members',
    personas: ['club-manager', 'outfitter-owner'],
  },
  {
    id: 'edocs',
    label: 'eDocs',
    icon: FileSignature,
    to: '/edocs',
    personas: ['club-manager', 'outfitter-owner'],
  },
  {
    id: 'settings',
    label: 'Settings',
    icon: Settings,
    to: '/settings',
    personas: ['club-manager', 'outfitter-owner'],
  },
]
